import DocumentPage from './DocumentPage';

export const calendarDocument = {
  slug: 'calendar',
  route: '/calendar',
  title: 'Calendar',
  category: 'Collaboration',
  format: 'DOCX',
  description:
    'The Calendar module in EduX is used to schedule, organize, and manage events, meetings, and activities with reminders and external calendar synchronization.',
  metrics: [
    { label: 'Primary focus', value: 'Event scheduling' },
    { label: 'Core records', value: 'Events and reminders' },
    { label: 'Coverage', value: 'Planning to sync' },
  ],
  workflow: [
    'Open Calendar to review daily, weekly, and monthly schedules.',
    'Create a new event with subject, time, attendees, and location details.',
    'Configure calendar settings and reminder rules for notifications and external synchronization.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'The Calendar Module in EduX is used to schedule, organize, and manage events, meetings, and activities. It helps users plan their daily, weekly, and monthly schedules efficiently.',
        'This module is useful for managing meetings, scheduling classes or sessions, tracking events and deadlines, and sending reminders to participants.',
      ],
      detailCards: [
        {
          title: 'This module is useful for',
          bullets: [
            'Managing meetings.',
            'Scheduling classes or sessions.',
            'Tracking events and deadlines.',
            'Sending reminders to participants.',
          ],
        },
      ],
    },
    {
      id: 'calendar-events',
      title: 'Calendar',
      body: [
        'Go to Calendar -> Calendar to open the event creation flow in the Calendar module.',
        'Users can schedule meetings by entering details such as subject, time, attendees, and location. The user can enter the meeting subject, set the date and time, add attendees, and configure reminders before saving the event.',
      ],
      figure: {
        src: '/assets/doc-images/calendar/image.png',
        alt: 'Calendar event form for a meeting',
        caption: 'Calendar event form showing subject, date, recurrence, attendees, reminders, and location details.',
      },
      detailCards: [
        {
          title: 'Event details',
          bullets: [
            'Meeting Subject: enter the title or name of the meeting.',
            'Start and End Time: set the event date and time.',
            'Attendees: add invited participants.',
            'Location: specify the meeting location or online meeting link.',
            'Reminders: configure event notifications before saving.',
          ],
        },
      ],
      steps: [
        'Go to Calendar -> Calendar.',
        'Click New to create a meeting or event.',
        'Enter the subject and set the event timing.',
        'Add attendees and the meeting location.',
        'Configure reminders if needed.',
        'Save the event.',
      ],
    },
    {
      id: 'configuration-settings',
      title: 'Configuration Settings',
      body: [
        'Go to Configuration -> Settings to access the Calendar Settings.',
        'Users can integrate external calendars like Outlook and Google Calendar with EduX for real-time event updates and better scheduling.',
      ],
      figure: {
        src: '/assets/doc-images/calendar/image2.png',
        alt: 'Calendar settings screen for external sync',
        caption: 'Calendar Settings page with Outlook and Google Calendar synchronization options.',
      },
      subsections: [
        {
          id: 'external-calendar-sync',
          title: 'External Calendar Integration',
          body: [
            'The Calendar Settings allow users to connect EduX calendar activity with external calendar platforms.',
          ],
          bullets: [
            'Outlook Calendar: synchronize your calendar with Outlook.',
            'Google Calendar: synchronize your calendar with Google Calendar.',
            'Enable synchronization options for real-time event updates.',
          ],
        },
      ],
    },
    {
      id: 'reminders',
      title: 'Remainder',
      body: [
        'Go to Configuration -> Remainder to access the Calendar Alarm settings.',
        'The Calendar Alarm settings define reminder types and timings for events. Users can select or configure reminders such as notifications or emails to be triggered before an event at specific intervals like minutes, hours, or days.',
      ],
      figure: {
        src: '/assets/doc-images/calendar/image3.png',
        alt: 'Calendar alarm reminder configuration',
        caption: 'Calendar Alarm settings showing reminder type and timing before the event.',
      },
      subsections: [
        {
          id: 'reminder-configuration',
          title: 'Reminder Configuration',
          body: [
            'Reminder records allow teams to decide how and when event notifications are sent.',
          ],
          bullets: [
            'Type: configure the reminder type such as Email.',
            'Remind Before: set the timing before the event, such as hours.',
            'Email Template: choose the message template used for reminder delivery.',
            'Use reminder rules to notify participants before important meetings or events.',
          ],
        },
      ],
    },
  ],
  related: ['to-dos', 'attendance', 'faculty'],
};

export default function CalendarPage() {
  return <DocumentPage document={calendarDocument} />;
}
